import { Token, authCallback, getToken, removeToken, root, setToken } from '@e2m-customer-portal/core';
import ClientOAuth2 from 'client-oauth2';
import { useCallback, useRef } from 'react';
import { useAsyncFn, useLocation } from 'react-use';
import { useAuthClient } from '../use-auth-client/use-auth-client';
import { useOpenIdConfig } from '../use-open-id-config/use-open-id-config';

const INTENDED_ROUTE_KEY = 'intended-route';

interface UseAuth {
  isLoggedIn: boolean;
  login: () => void;
  redeemAuthCode: () => void;
  logout: () => void;
}

function redirectToLogoutPage(
  logoutUrl: string,
  authority: string,
  tokenData: Token | null,
  client: ClientOAuth2,
  protocol?: string,
) {
  if (logoutUrl) {
    const redirectUrl = `${protocol}${authority}${authCallback.getLink()}`;
    const params = new URLSearchParams();
    params.append('post_logout_redirect_uri', redirectUrl);
    params.append('id_token_hint', tokenData?.id_token ?? '');

    // redirect to logout page of the auth provider, so it can revoke the session
    window.location.replace(`${logoutUrl}?${params.toString()}`);
  } else {
    // some auth providers do not have logout, sessions time out implicitly - go back to login page instead
    window.location.replace(client.code.getUri());
  }
}

export const useAuth = Object.assign(
  (): UseAuth => {
    const { protocol, hostname, port, search } = useLocation();
    const isLoggedIn = useRef(getToken() != null).current;
    const config = useOpenIdConfig();
    const client = useAuthClient();

    const login = useCallback(
      (saveCurrentPath = true): void => {
        removeToken();
        if (saveCurrentPath) {
          sessionStorage.setItem(INTENDED_ROUTE_KEY, window.location.pathname);
        }
        window.location.replace(client.code.getUri());
      },
      [client],
    );

    const [{ error }, redeemAuthCode] = useAsyncFn(async () => {
      const query = new URLSearchParams(search);
      if (!query.has('session_state') || !query.has('code')) {
        // redirect after logout, go back to login page
        login(false);
        return;
      }

      if (useAuth.hasRedeemedCode) {
        // code was already redeemed, or is in the progress of being redeemed
        return;
      }

      useAuth.hasRedeemedCode = true;
      try {
        const tokenResponse = await client.code.getToken(window.location);
        setToken({
          access_token: tokenResponse.accessToken,
          refresh_token: tokenResponse.refreshToken,
          id_token: tokenResponse.data.id_token,
        });

        // reload page instead of history push to not have to worry about React state
        const nextUrl = sessionStorage.getItem(INTENDED_ROUTE_KEY) ?? root.getLink();
        sessionStorage.removeItem(INTENDED_ROUTE_KEY);
        window.location.replace(nextUrl);
      } catch (err) {
        useAuth.hasRedeemedCode = false;
        throw err;
      }
    }, [client, login, search]);

    if (error) {
      throw error;
    }

    const logout = useCallback((): void => {
      const tokenData = getToken();
      removeToken();

      const logoutUrl = config.end_session_endpoint;
      let authority = `//${hostname}`;
      if (port) {
        authority += `:${port}`;
      }

      sessionStorage.setItem(INTENDED_ROUTE_KEY, window.location.pathname);
      redirectToLogoutPage(logoutUrl, authority, tokenData, client, protocol);
    }, [client, config.end_session_endpoint, hostname, port, protocol]);

    return {
      isLoggedIn,
      login,
      redeemAuthCode: redeemAuthCode as () => void,
      logout,
    };
  },
  {
    /**
     * Whether the login code has been redeemed for an access token. The access token is a global URL parameter,
     * thus this variable is a global, too.
     */
    hasRedeemedCode: false,
  },
);
